import { useRef, useEffect, useState, useCallback, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import EventBus from '@interhyp/util-eventbus';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import fetchWrapper, { getConnection } from '@interhyp/util-request';

/**
 * This custom hook can be used to access the previous value of a property.
 */
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function useNavigate() {
  return useHistory().push;
}

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var loadAnimationEvent = 'load-animation-completed';

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function useEmitLoadAnimationCompleted() {
  useEffect(function () {
    EventBus.emit(loadAnimationEvent);
  }, []);
}

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function useABTest(key) {
  var _useState = useState(false),
    isFlagActive = _useState[0],
    setIsFlagActive = _useState[1];
  useEffect(function () {
    var _window$abtest;
    ((_window$abtest = window.abtest) == null ? void 0 : _window$abtest[key]) && setIsFlagActive(true);
  }, []);
  return isFlagActive;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/*
  this method is in a separate file for better testability because
  in jest we cant mock a only a single method from an module
 */

var handleResizeObserverAsync = function handleResizeObserverAsync(element) {
  return setTimeout(function () {
    var _element$target;
    return element == null ? void 0 : (_element$target = element.target) == null ? void 0 : _element$target.handleResizeObserver(element);
  });
};

var lastSuccessEventCall = 0;
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var BLOCKING_TIME = 33.3;

/**
 * Call handleResizeObserver() on HTMLElement
 * with current element as prop
 *
 * @param {Array<React.MutableRefObject<HTMLElement | null>>} entries
 */
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var resizeObserverCallback = function resizeObserverCallback(entries) {
  var currentTime = new Date().getTime();
  var blockTime = lastSuccessEventCall + BLOCKING_TIME;

  // buffer call for 33.3ms
  if (blockTime > currentTime) {
    return;
  }
  lastSuccessEventCall = currentTime;
  for (var _iterator = _createForOfIteratorHelperLoose(entries), _step; !(_step = _iterator()).done;) {
    var entry = _step.value;
    handleResizeObserverAsync(entry);
  }
};

/**
 * Because of currently Performance Issues of ResizeObserver, only one Instance should be available
 *
 * Links:
 * https://groups.google.com/a/chromium.org/forum/#!msg/blink-dev/z6ienONUb5A/F5-VcUZtBAAJ
 * https://github.com/WICG/ResizeObserver/issues/59#issuecomment-408098151
 */
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var ResizeObserver = new ResizeObserverPolyfill(resizeObserverCallback);

/**
 * Find the largest BreakPoint of given width
 *
 * @param {Array.<{}>} breakpoints
 * @param {Number} width
 * @returns {string}
 */
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var findBreakPoint = function findBreakPoint(breakpoints, width) {
  if (width === void 0) {
    width = 0;
  }
  var breakpointIndex = breakpoints.map(function (x) {
    return Object.values(x)[0];
  }).findIndex(function (x) {
    return width < x;
  });

  // element is larger than every breakpoint so it must be the last breakpoint
  if (breakpointIndex === -1) {
    return Object.keys(breakpoints[breakpoints.length - 1])[0];
  }
  return Object.keys(breakpoints[breakpointIndex])[0];
};

/**
 *  Use ResizeObserver for dynamic styling of your component
 *
 *  Example:
 *  const size = useResponsiveBreakpoints(elRef, [
 *     { [BREAKPOINT_NAMES.EXTRA_SMALL]: 370 },
 *     { [BREAKPOINT_NAMES.SMALL]: 380 },
 *     { [BREAKPOINT_NAMES.MEDIUM]: 410 },
 *     { [BREAKPOINT_NAMES.LARGE]: 500 },
 *  ]); => 'BREAKPOINT_NAMES.SMALL'
 *
 * @param {React.MutableRefObject<HTMLElement | null>} elRef
 * @param {Array.<{}>} breakpoints
 * @returns {string}
 */
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var useResponsiveBreakpoints = function useResponsiveBreakpoints(elRef, breakpoints) {
  var firstQuery = Object.keys(breakpoints[0])[0];
  var _useState = useState(firstQuery),
    breakSize = _useState[0],
    setBreakSize = _useState[1];
  var _useState2 = useState(null),
    currentEl = _useState2[0],
    setCurrentEl = _useState2[1];
  useEffect(function () {
    if (elRef && elRef.current !== currentEl) {
      setCurrentEl(elRef.current);
    }
  });
  useEffect(function () {
    if (!currentEl) {
      return;
    }
    currentEl.handleResizeObserver = function (e) {
      var width = Math.round(e.contentRect.width);
      setBreakSize(findBreakPoint(breakpoints, width));
    };
  }, [currentEl, breakpoints]);
  useEffect(function () {
    if (!currentEl) {
      return;
    }
    ResizeObserver.observe(currentEl);
    return function () {
      return ResizeObserver.unobserve(currentEl);
    };
  }, [currentEl]);
  return breakSize;
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var BREAKPOINT_NAMES = {
  EXTRA_SMALL: 'EXTRA_SMALL',
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  EXTRA_LARGE: 'EXTRA_LARGE'
};

var ERROR_TYPE = {
  GENERIC_ERROR: 'Something went wrong',
  CLIENT_ERROR: {
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Not Allowed',
    408: 'Request Timeout'
  },
  SERVER_ERROR: {
    501: 'Not Implemented',
    502: 'Bad Gateway',
    504: 'Not Found',
    505: 'Not Allowed',
    508: 'Request Timeout'
  }
};
var forcedReducer = function forcedReducer(state) {
  return state + 1;
};
var useForceUpdate = function useForceUpdate() {
  return useReducer(forcedReducer, 0)[1];
};
var createTask = function createTask(func, forceUpdateRef) {
  var task = {
    start: function () {
      var _start = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var _args = arguments;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                task.status = 'fetching';
                task.loading = true;
                task.result = undefined;
                forceUpdateRef.current(func);
                _context.prev = 4;
                _context.next = 7;
                return func.apply(void 0, _args);
              case 7:
                task.result = _context.sent;
                task.status = 'fetched';
                task.error = undefined;
                _context.next = 16;
                break;
              case 12:
                _context.prev = 12;
                _context.t0 = _context["catch"](4);
                task.status = ERROR_TYPE.CLIENT_ERROR[_context.t0] || ERROR_TYPE.GENERIC_ERROR;
                task.error = _context.t0;
              case 16:
                task.loading = false;
                forceUpdateRef.current(func);
                return _context.abrupt("return", task);
              case 19:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[4, 12]]);
      }));
      function start() {
        return _start.apply(this, arguments);
      }
      return start;
    }(),
    status: 'idle',
    loading: false,
    result: undefined,
    error: undefined
  };
  return task;
};
var consolidateData = function consolidateData(initial) {
  var fetchOptions = {
    cancelRequest: false,
    url: "/" + (initial == null ? void 0 : initial.url),
    method: (initial == null ? void 0 : initial.method) || 'GET',
    body: JSON.stringify(initial == null ? void 0 : initial.body)
  };
  if (fetchOptions.method === 'GET') {
    delete fetchOptions.body;
  }
  return fetchOptions;
};
var fetchApi = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(options, cache) {
    var _consolidateData, url, method, body, cancelRequest, response, data;
    return regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _consolidateData = consolidateData(options), url = _consolidateData.url, method = _consolidateData.method, body = _consolidateData.body, cancelRequest = _consolidateData.cancelRequest;
            if (!(cache && cache.current[url])) {
              _context2.next = 3;
              break;
            }
            return _context2.abrupt("return", cache.current[url]);
          case 3:
            _context2.prev = 3;
            _context2.next = 6;
            return fetchWrapper(getConnection(), url, {
              method: method,
              body: body
            });
          case 6:
            response = _context2.sent;
            if (!(response.status >= 200 && response.status < 300)) {
              _context2.next = 13;
              break;
            }
            _context2.next = 10;
            return response.json();
          case 10:
            data = _context2.sent;
            if (cache && cache.current[url]) {
              cache.current[url] = data;
            }
            return _context2.abrupt("return", data);
          case 13:
            if (!(response.status >= 400)) {
              _context2.next = 15;
              break;
            }
            throw response.status;
          case 15:
            _context2.next = 22;
            break;
          case 17:
            _context2.prev = 17;
            _context2.t0 = _context2["catch"](3);
            if (!cancelRequest) {
              _context2.next = 21;
              break;
            }
            return _context2.abrupt("return", null);
          case 21:
            throw _context2.t0;
          case 22:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[3, 17]]);
  }));
  return function fetchApi(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var useAsyncTask = function useAsyncTask(func) {
  var forceUpdate = useForceUpdate();
  var forceUpdateRef = useRef(forceUpdate);
  var task = useMemo(function () {
    return createTask(func, forceUpdateRef);
  }, [func]);
  useEffect(function () {
    forceUpdateRef.current = function (f) {
      if (f === func) {
        forceUpdate();
      }
    };
    var cleanup = function cleanup() {
      forceUpdateRef.current = function () {
        return null;
      };
    };
    return cleanup;
  }, [func, forceUpdate]);
  return useMemo(function () {
    return {
      start: task.start,
      loading: task.loading,
      error: task.error,
      result: task.result,
      status: task.status
    };
  }, [task.start, task.loading, task.error, task.result, task.status]);
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var useFetch = function useFetch(initial, useCache) {
  if (useCache === void 0) {
    useCache = false;
  }
  var cache = useRef({});
  return useAsyncTask(useCallback(function (overrides) {
    return fetchApi(_extends({}, initial, overrides), useCache ? cache : undefined);
  }, [!!initial]));
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var useAsyncRun = function useAsyncRun(asyncTask) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  var start = asyncTask.start;
  useEffect(function () {
    start.apply(void 0, args);
  }, [asyncTask.start].concat(args));
  useEffect(function () {
    var cleanup = function cleanup() {
      // clean up code here
    };
    return cleanup;
  });
};

var listeners = new Set();
var state = {
  unitId: 'me'
};
var setState = function setState(newState) {
  var firstKey = Object.keys(newState)[0];
  Object.keys(state).some(function (key) {
    if (key === firstKey && state[key] !== newState[firstKey]) {
      state = _extends({}, state, newState);
      listeners.forEach(function (listener) {
        listener(state);
      });
    }
  });
};
var useGlobal = function useGlobal() {
  var _useState = useState(),
    setNewListener = _useState[1];
  useEffect(function () {
    listeners.add(setNewListener);
    return function () {
      if (listeners.has(setNewListener)) {
        listeners["delete"](setNewListener);
      }
    };
  }, []);
  return [state, setState];
};

export { BREAKPOINT_NAMES, ResizeObserver, loadAnimationEvent, useABTest, useAsyncRun, useEmitLoadAnimationCompleted, useFetch, useGlobal, useNavigate, usePrevious, useResponsiveBreakpoints };
//# sourceMappingURL=index.js.map
